import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { HeartPulse, Plus, Eye, Check, X, Truck, PackageCheck, AlertTriangle, Pencil, Pill, Trash2, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DraftBanner } from '../components/DraftBanner';
import { useFormDraft } from '../hooks/useFormDraft';
import { fmtDate, fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function userCan(user, module, action) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[module]?.[action] === true;
}

const STATUS_LABELS = {
  submitted: { label: 'Pendiente Aprobación', cls: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Aprobado', cls: 'bg-amber-100 text-amber-800' },
  partial_dispensed: { label: 'Despacho Parcial', cls: 'bg-yellow-100 text-yellow-800 border border-yellow-300' },
  dispensed: { label: 'Despachado', cls: 'bg-teal-100 text-teal-800' },
  closed_partial: { label: 'Cerrado c/ saldo', cls: 'bg-slate-200 text-slate-800' },
  closed: { label: 'Recibido', cls: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rechazado', cls: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelado', cls: 'bg-slate-200 text-slate-700' },
};

const PRIORITY_LABELS = {
  baja: { label: 'Baja', cls: 'bg-slate-100 text-slate-700' },
  media: { label: 'Media', cls: 'bg-blue-100 text-blue-800' },
  alta: { label: 'Alta', cls: 'bg-orange-100 text-orange-800' },
  urgente: { label: 'Urgente', cls: 'bg-red-100 text-red-800' },
};

function InternalRequests() {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filterAreaId, setFilterAreaId] = useState(searchParams.get('pending_receipt') === '1' ? '__all__' : '');
  const [onlyAwaiting, setOnlyAwaiting] = useState(searchParams.get('pending_receipt') === '1');
  const [warehouses, setWarehouses] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    area_id: '', source_warehouse_id: '', priority: 'media', required_date: '', notes: '', items: []
  });
  const [newItem, setNewItem] = useState({ article_id: '', article_name: '', quantity_requested: '' });
  const [articleSelectOpen, setArticleSelectOpen] = useState(false);
  const [fefoPreview, setFefoPreview] = useState({});
  const { drafts, loadDrafts, getDraft, saveDraft, deleteDraft } = useFormDraft('opi');
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftInfo, setDraftInfo] = useState(null);

  const catLabel = (a) => a?.category || (a?.is_medicamento ? 'Medicamento' : 'Insumo');

  const [detailDialog, setDetailDialog] = useState(false);
  const [selected, setSelected] = useState(null);

  const [approveDialog, setApproveDialog] = useState(false);
  const [approveForm, setApproveForm] = useState({ items: {}, approval_notes: '' });
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const canCreate = userCan(user, 'pedidos_internos', 'crear');
  const canApprove = userCan(user, 'pedidos_internos', 'aprobar');
  const canDispense = userCan(user, 'pedidos_internos', 'despachar');
  const canReceive = userCan(user, 'pedidos_internos', 'recibir');
  const canDelete = userCan(user, 'pedidos_internos', 'eliminar');
  const canClosePending = userCan(user, 'pedidos_internos', 'cerrar_con_pendiente');

  // "Cerrar c/ saldo pendiente" modal
  const [closePendingOpi, setClosePendingOpi] = useState(null);
  const [closePendingReason, setClosePendingReason] = useState('');
  const [closePendingSaving, setClosePendingSaving] = useState(false);

  const submitClosePending = async () => {
    if (!closePendingOpi || !closePendingReason.trim()) {
      toast.error('Tenés que indicar un motivo');
      return;
    }
    setClosePendingSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/internal-requests/${closePendingOpi.request_id}/close-with-pending`,
        { reason: closePendingReason.trim() },
        { withCredentials: true }
      );
      toast.success(`OPI ${closePendingOpi.request_number} cerrada con saldo pendiente`);
      setClosePendingOpi(null);
      setClosePendingReason('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al cerrar la OPI');
    } finally {
      setClosePendingSaving(false);
    }
  };

  useEffect(() => {
    fetchAll();
    if (userCan(user, 'pedidos_internos', 'crear')) loadDrafts();
  }, []);

  const fetchAll = async () => {
    try {
      const [r, a, w, art] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/internal-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/internal-areas`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/warehouses`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/articles`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setRequests(r.data || []);
      setAreas((a.data || []).filter(x => x.is_active));
      setWarehouses(w.data || []);
      setArticles(art.data || []);
    } catch (e) {
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ area_id: '', source_warehouse_id: '', priority: 'media', required_date: '', notes: '', items: [] });
    setNewItem({ article_id: '', article_name: '', quantity_requested: '' });
    setFefoPreview({});
    setEditing(null);
    setDraftInfo(null);
  };

  const openNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const areaSources = (area) => {
    if (!area) return [];
    const ids = (area.source_warehouse_ids || []).filter(Boolean);
    if (ids.length) return ids;
    return area.source_warehouse_id ? [area.source_warehouse_id] : [];
  };

  const openEdit = (req) => {
    setEditing(req);
    setForm({
      area_id: req.area_id,
      source_warehouse_id: req.source_warehouse_id || '',
      priority: req.priority || 'media',
      required_date: req.required_date || '',
      notes: req.notes || '',
      items: (req.items || []).map(it => ({
        article_id: it.article_id,
        article_name: it.article_name,
        quantity_requested: it.quantity_requested,
      })),
    });
    setDialogOpen(true);
  };

  const onAreaChange = async (areaId) => {
    const area = areas.find(a => a.area_id === areaId);
    const srcs = areaSources(area);
    // Auto-seleccionar el origen si el área tiene uno solo (UX limpia); si tiene varios, forzar elección.
    const src = srcs.length === 1 ? srcs[0] : '';
    setForm({ ...form, area_id: areaId, source_warehouse_id: src, items: [] });
    setDraftInfo(null);
    if (!areaId || editing) return;
    const d = await getDraft(areaId);
    if (d?.payload?.items?.length) applyDraft(d);
  };

  const applyDraft = (d) => {
    const p = d.payload || {};
    setForm({
      area_id: d.area_id,
      source_warehouse_id: p.source_warehouse_id || '',
      priority: p.priority || 'media',
      required_date: p.required_date || '',
      notes: p.notes || '',
      items: p.items || [],
    });
    setEditing(null);
    setDraftInfo(d);
    setDialogOpen(true);
    toast.info(`Retomando el borrador guardado por ${d.updated_by_name}`);
  };

  const doSaveDraft = async (silent = false) => {
    if (!form.area_id || editing) return;
    setDraftSaving(true);
    const d = await saveDraft(form.area_id, {
      source_warehouse_id: form.source_warehouse_id,
      priority: form.priority,
      required_date: form.required_date,
      notes: form.notes,
      items: form.items,
    });
    setDraftSaving(false);
    if (d) {
      setDraftInfo(d);
      if (!silent) toast.success('Borrador guardado: podés cerrar y seguir después');
    } else if (!silent) {
      toast.error('No se pudo guardar el borrador');
    }
  };

  // Autoguardado cada 30 s mientras se carga (por si interrumpen al usuario).
  useEffect(() => {
    if (!dialogOpen || editing || !form.area_id || form.items.length === 0) return;
    const t = setInterval(() => { doSaveDraft(true); }, 30000);
    return () => clearInterval(t);
  }, [dialogOpen, editing, form]);  // eslint-disable-line react-hooks/exhaustive-deps

  const discardDraft = async (d) => {
    await deleteDraft(d.area_id);
    if (form.area_id === d.area_id) setDraftInfo(null);
    toast.success('Borrador descartado');
  };

  const selectedArea = areas.find(a => a.area_id === form.area_id);
  const selectedAreaSources = areaSources(selectedArea);

  const filteredArticles = (() => {
    const wh = form.source_warehouse_id;
    if (!wh) return [];
    return articles.filter(a => a.warehouse_id === wh && !a.is_archived);
  })();

  const refreshFefo = useCallback(async (items, warehouseId) => {
    if (!warehouseId || items.length === 0) {
      setFefoPreview({});
      return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/internal-requests/preview-fefo`,
        { source_warehouse_id: warehouseId, items: items.map(i => ({ article_id: i.article_id, quantity_requested: i.quantity_requested })) },
        { withCredentials: true }
      );
      const map = {};
      (res.data?.items || []).forEach(p => { map[p.article_id] = p; });
      setFefoPreview(map);
    } catch (e) {
      // silencioso
    }
  }, []);

  useEffect(() => {
    if (dialogOpen) refreshFefo(form.items, form.source_warehouse_id);
  }, [form.items, form.source_warehouse_id, dialogOpen, refreshFefo]);

  const unitOf = (articleId) => articles.find(a => a.article_id === articleId)?.unit || 'unidad';

  const addItem = () => {
    if (!newItem.article_id || !newItem.quantity_requested || newItem.quantity_requested < 1) {
      toast.error('Selecciona artículo y cantidad');
      return;
    }
    if (form.items.some(i => i.article_id === newItem.article_id)) {
      toast.error('Ese artículo ya está en el pedido');
      return;
    }
    const art = articles.find(a => a.article_id === newItem.article_id);
    setForm({
      ...form,
      items: [...form.items, {
        article_id: newItem.article_id,
        article_name: art?.name || '',
        quantity_requested: parseInt(newItem.quantity_requested),
      }],
    });
    setNewItem({ article_id: '', article_name: '', quantity_requested: '' });
  };

  const removeItem = (idx) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.area_id || form.items.length === 0) {
      toast.error('Completá el área y al menos un artículo');
      return;
    }
    if (selectedAreaSources.length > 1 && !form.source_warehouse_id) {
      toast.error('Elegí el depósito origen del pedido');
      return;
    }
    try {
      const payload = {
        area_id: form.area_id,
        source_warehouse_id: form.source_warehouse_id || null,
        priority: form.priority,
        required_date: form.required_date || null,
        notes: form.notes,
        items: form.items.map(i => ({ article_id: i.article_id, quantity_requested: i.quantity_requested })),
      };
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/internal-requests/${editing.request_id}`, payload, { withCredentials: true });
        toast.success('Pedido actualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/internal-requests`, payload, { withCredentials: true });
        toast.success('Pedido enviado');
        await deleteDraft(form.area_id);
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const openDetail = async (req) => {
    setSelected(req);
    setDetailDialog(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/internal-requests/${req.request_id}`, { withCredentials: true });
      setSelected(r.data);
    } catch (e) { /* mantiene el dato de la lista si falla */ }
  };


  const openApprove = (req) => {
    setSelected(req);
    const m = {};
    req.items.forEach(it => { m[it.article_id] = it.quantity_requested; });
    setApproveForm({ items: m, approval_notes: '' });
    setApproveDialog(true);
  };

  const doApprove = async () => {
    try {
      const itemsPayload = Object.entries(approveForm.items).map(([aid, qty]) => ({
        article_id: aid,
        quantity_approved: parseInt(qty) || 0,
      }));
      await axios.put(
        `${BACKEND_URL}/api/internal-requests/${selected.request_id}/approve`,
        { items: itemsPayload, approval_notes: approveForm.approval_notes },
        { withCredentials: true }
      );
      toast.success('Pedido aprobado');
      setApproveDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const doReject = async (req) => {
    const reason = prompt('Motivo de rechazo:');
    if (!reason) return;
    try {
      await axios.put(`${BACKEND_URL}/api/internal-requests/${req.request_id}/reject`, { reason }, { withCredentials: true });
      toast.success('Pedido rechazado');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const doDispense = (req) => setConfirmAction({ type: 'dispense', req });
  const doConfirmReceipt = (req) => setConfirmAction({ type: 'receipt', req });
  const doDelete = (req) => setConfirmAction({ type: 'delete', req });

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, req } = confirmAction;
    setActionLoading(true);
    try {
      if (type === 'dispense') {
        await axios.put(`${BACKEND_URL}/api/internal-requests/${req.request_id}/dispense`, {}, { withCredentials: true });
        toast.success('Pedido despachado');
      } else if (type === 'delete') {
        await axios.delete(`${BACKEND_URL}/api/internal-requests/${req.request_id}`, { withCredentials: true });
        toast.success('Pedido eliminado');
      } else {
        await axios.put(`${BACKEND_URL}/api/internal-requests/${req.request_id}/confirm-receipt`, {}, { withCredentials: true });
        toast.success('Recepción confirmada');
      }
      setConfirmAction(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setActionLoading(false);
    }
  };

  const baseVisible = filterAreaId === '__all__'
    ? requests
    : (filterAreaId ? requests.filter(r => r.area_id === filterAreaId) : []);
  const visibleRequests = onlyAwaiting ? baseVisible.filter(r => r.awaiting_receipt) : baseVisible;

  const clearAwaitingFilter = () => {
    setOnlyAwaiting(false);
    searchParams.delete('pending_receipt');
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <HeartPulse className="w-7 h-7 text-rose-500" /> Pedidos Internos (OPI)
        </h1>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-opi-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Pedido
          </Button>
        )}
      </div>

      {canCreate && (
        <DraftBanner
          drafts={drafts}
          label="Borrador de OPI en curso"
          onContinue={applyDraft}
          onDiscard={discardDraft}
          testid="opi-draft-banner"
        />
      )}

      {onlyAwaiting && (        <div className="flex items-center justify-between gap-3 rounded-lg border border-teal-300 bg-teal-50 px-4 py-3 text-teal-800" data-testid="opi-awaiting-filter-banner">
          <div className="flex items-center gap-2 text-sm">
            <PackageCheck className="w-4 h-4 shrink-0" />
            <span>Mostrando solo <b>OPIs despachadas pendientes de recepción</b> ({visibleRequests.length}). Cada área debe confirmar la recepción para que el stock ingrese.</span>
          </div>
          <Button variant="outline" size="sm" onClick={clearAwaitingFilter} data-testid="opi-clear-awaiting-filter">Ver todas</Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <CardTitle>Listado</CardTitle>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Filtrar por área</label>
              <select
                value={filterAreaId}
                onChange={(e) => setFilterAreaId(e.target.value)}
                className="flex h-10 min-w-[240px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="opi-filter-area"
              >
                <option value="">— Seleccioná un área —</option>
                <option value="__all__">Todas las áreas</option>
                {areas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p>Cargando...</p> : !filterAreaId ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="opi-pick-area-prompt">
              <HeartPulse className="w-10 h-10 mx-auto mb-3 text-rose-200" />
              <p>Seleccioná un área arriba para ver sus OPIs.</p>
              <p className="text-xs mt-1">(O elegí «Todas las áreas» para ver el listado completo.)</p>
            </div>
          ) : visibleRequests.length === 0 ? (
            <p className="text-muted-foreground" data-testid="opi-empty">Sin pedidos para {filterAreaId === '__all__' ? 'mostrar' : 'esta área'}.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Solicitante</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRequests.map(r => {
                  const status = STATUS_LABELS[r.status] || { label: r.status, cls: 'bg-slate-100' };
                  const prio = PRIORITY_LABELS[r.priority] || PRIORITY_LABELS.media;
                  const isOwner = r.created_by === user?.user_id;
                  return (
                    <TableRow key={r.request_id}>
                      <TableCell className="font-mono text-xs">{r.request_number}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {r.created_at ? fmtDate(r.created_at, { year: '2-digit' }) : '-'}
                      </TableCell>
                      <TableCell>{r.area_name}</TableCell>
                      <TableCell className="text-sm">{r.created_by_name || '-'}</TableCell>
                      <TableCell><Badge className={prio.cls}>{prio.label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{(r.items || []).length}</span>
                          {r.has_shortage && <AlertTriangle className="w-4 h-4 text-amber-500" title="Stock insuficiente" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={status.cls}>{status.label}</Badge>
                          {r.awaiting_receipt && (
                            <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-300 text-xs" title="Despachada: el área todavía no confirmó la recepción. El stock está en tránsito." data-testid={`opi-awaiting-badge-${r.request_id}`}>
                              <PackageCheck className="w-3 h-3 mr-1" /> Esperando recepción
                            </Badge>
                          )}
                          {r.procurement_status === 'pending_purchase' && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs" title={`Vinculada a ${r.linked_purchase_request_number || 'NP'}`}>
                              🛒 En compra
                            </Badge>
                          )}
                          {r.procurement_status === 'received' && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs" title={`Recibida ${r.linked_reception_number || ''}`}>
                              ✅ Stock recibido
                            </Badge>
                          )}
                          {r.procurement_status === 'externally_resolved' && (
                            <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs" title={r.externally_resolved_reason || ''}>
                              ↗ Atendida afuera
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openDetail(r)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {r.status === 'submitted' && isOwner && (
                            <Button variant="ghost" size="icon" onClick={() => openEdit(r)} title="Editar">
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                          {r.status === 'submitted' && canApprove && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => openApprove(r)} title="Aprobar">
                                <Check className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => doReject(r)} title="Rechazar">
                                <X className="w-4 h-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          {(r.status === 'approved' || r.status === 'partial_dispensed') && canDispense && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => doDispense(r)}
                              title={r.status === 'partial_dispensed' ? 'Despachar saldo pendiente' : 'Despachar (lo disponible)'}
                              data-testid={`dispense-${r.request_id}`}
                            >
                              <Truck className={`w-4 h-4 ${r.status === 'partial_dispensed' ? 'text-yellow-600' : 'text-teal-600'}`} />
                            </Button>
                          )}
                          {r.status === 'partial_dispensed' && canClosePending && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setClosePendingOpi(r)}
                              title="Cerrar OPI renunciando al saldo pendiente"
                              data-testid={`close-pending-${r.request_id}`}
                            >
                              <X className="w-4 h-4 text-slate-500" />
                            </Button>
                          )}
                          {(r.status === 'dispensed' || r.status === 'partial_dispensed' || r.status === 'closed_partial') && !r.received_at_area && (canReceive || canApprove) && (
                            <Button variant="ghost" size="icon" onClick={() => doConfirmReceipt(r)} title="Confirmar recepción en área" data-testid={`confirm-receipt-${r.request_id}`}>
                              <PackageCheck className="w-4 h-4 text-emerald-700" />
                            </Button>
                          )}
                          {(r.status === 'dispensed' || r.status === 'partial_dispensed' || r.status === 'closed_partial') && r.received_at_area && (
                            <span className="inline-flex items-center text-xs text-emerald-700" title={`Recibido en área${r.received_by_area_name ? ' por ' + r.received_by_area_name : ''}`} data-testid={`received-badge-${r.request_id}`}>
                              <PackageCheck className="w-4 h-4" />
                            </span>
                          )}
                          {r.status === 'submitted' && (isOwner || canDelete || canApprove) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => doDelete(r)}
                              title="Eliminar pedido (sólo no aprobados)"
                              data-testid={`delete-opi-${r.request_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Pedido Interno' : 'Nuevo Pedido Interno'}</DialogTitle>
            <DialogDescription>El pedido se envía directamente a aprobación.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {draftInfo && !editing && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200" data-testid="opi-draft-note">
                Borrador guardado por {draftInfo.updated_by_name} · {fmtDateTime(draftInfo.updated_at)}. Se guarda solo cada 30 segundos; recién al <b>Enviar a Aprobación</b> se genera la OPI.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Área *</Label>
                <select
                  value={form.area_id}
                  onChange={(e) => onAreaChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                  disabled={!!editing}
                  data-testid="opi-area"
                >
                  <option value="">Selecciona...</option>
                  {areas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">El depósito destino se toma de la configuración del área.</p>
              </div>
              {form.area_id && selectedAreaSources.length > 1 && (
                <div>
                  <Label>Depósito origen del pedido *</Label>
                  <select
                    value={form.source_warehouse_id}
                    onChange={(e) => setForm({ ...form, source_warehouse_id: e.target.value, items: [] })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    disabled={!!editing}
                    data-testid="opi-source-warehouse"
                  >
                    <option value="">Selecciona el central...</option>
                    {selectedAreaSources.map(id => {
                      const w = warehouses.find(x => x.warehouse_id === id);
                      const name = w?.name || selectedArea?.source_warehouse_names?.[id] || id;
                      return <option key={id} value={id}>{name}</option>;
                    })}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">Esta área pide de varios centrales. Generá una OPI por cada central.</p>
                </div>
              )}
              <div>
                <Label>Prioridad</Label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div>
                <Label>Fecha requerida</Label>
                <Input type="date" value={form.required_date} onChange={(e) => setForm({ ...form, required_date: e.target.value })} />
              </div>
            </div>

            <div>
              <Label>Observaciones</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            {form.area_id && !form.source_warehouse_id && selectedAreaSources.length > 1 && (
              <p className="text-sm text-amber-600">Elegí el depósito origen del pedido para cargar artículos.</p>
            )}

            {form.area_id && form.source_warehouse_id && (
              <div className="border rounded-md p-3 space-y-2">
                <Label className="text-base">Agregar artículo</Label>
                <div className="flex gap-2">
                  <Popover open={articleSelectOpen} onOpenChange={setArticleSelectOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="flex-1 justify-between font-normal"
                        data-testid="opi-article-select"
                      >
                        {newItem.article_id
                          ? (filteredArticles.find(a => a.article_id === newItem.article_id)?.name || 'Artículo')
                          : <span className="text-muted-foreground">Buscar artículo por nombre...</span>}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                        <CommandInput placeholder="Escribí el nombre del artículo..." data-testid="opi-article-search" />
                        <CommandList>
                          <CommandEmpty>Sin resultados.</CommandEmpty>
                          <CommandGroup>
                            {filteredArticles.map(a => (
                              <CommandItem
                                key={a.article_id}
                                value={`${a.name} ${catLabel(a)}`}
                                onSelect={() => { setNewItem({ ...newItem, article_id: a.article_id, article_name: a.name }); setArticleSelectOpen(false); }}
                                data-testid={`opi-article-option-${a.article_id}`}
                                className="flex items-center justify-between gap-2"
                              >
                                <span className="flex items-center gap-2">
                                  {a.is_medicamento && <Pill className="w-3.5 h-3.5 text-teal-600 shrink-0" />}
                                  <span>{a.name}</span>
                                </span>
                                <span className="flex items-center gap-2 shrink-0">
                                  <Badge variant="outline" className={`text-[10px] ${catLabel(a).toLowerCase().includes('externo') ? 'bg-rose-50 text-rose-700 border-rose-300' : 'bg-teal-50 text-teal-700 border-teal-300'}`}>
                                    {catLabel(a)}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">stock: {a.stock} {a.unit || 'unidad'}</span>
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="number"
                    min="1"
                    placeholder="Cant."
                    value={newItem.quantity_requested}
                    onChange={(e) => setNewItem({ ...newItem, quantity_requested: e.target.value })}
                    className="w-24"
                    data-testid="opi-qty"
                  />
                  {newItem.article_id && (
                    <span className="self-center text-xs text-muted-foreground whitespace-nowrap" data-testid="opi-qty-unit">
                      {unitOf(newItem.article_id)}
                    </span>
                  )}
                  <Button type="button" onClick={addItem} data-testid="opi-add-item"><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
            )}

            {form.items.length > 0 && (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artículo</TableHead>
                      <TableHead className="text-center">Pedido</TableHead>
                      <TableHead className="text-center">Stock</TableHead>
                      <TableHead>Preview FEFO</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => {
                      const preview = fefoPreview[it.article_id];
                      const stock = preview?.available_stock ?? '—';
                      const shortage = preview?.shortage ?? 0;
                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {preview?.is_medicamento && <Pill className="w-4 h-4 text-rose-500" />}
                              {it.article_name}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{it.quantity_requested} <span className="text-xs text-muted-foreground">{unitOf(it.article_id)}</span></TableCell>
                          <TableCell className="text-center">{stock}</TableCell>
                          <TableCell className="text-xs">
                            {preview?.allocations?.length > 0 ? (
                              <div className="space-y-0.5">
                                {preview.allocations.map((a, i) => (
                                  <div key={i}>
                                    Lote <b>{a.lot_number || 's/lote'}</b> ({a.expiration_date || '—'}) → {a.qty}u
                                  </div>
                                ))}
                                {shortage > 0 && <div className="text-red-600 font-medium">Faltan {shortage}u</div>}
                              </div>
                            ) : <span className="text-muted-foreground">{shortage > 0 ? `Sin stock (faltan ${shortage}u)` : '—'}</span>}
                          </TableCell>
                          <TableCell>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              {!editing && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => doSaveDraft(false)}
                  disabled={!form.area_id || form.items.length === 0 || draftSaving}
                  data-testid="opi-save-draft"
                >
                  {draftSaving ? 'Guardando…' : 'Guardar borrador'}
                </Button>
              )}
              <Button type="submit" disabled={form.items.length === 0} data-testid="opi-submit">
                {editing ? 'Guardar' : 'Enviar a Aprobación'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detalle */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected?.request_number}</DialogTitle>
            <DialogDescription>{selected?.area_name}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Solicitó:</span> {selected.created_by_name} <span className="text-xs text-muted-foreground">({selected.created_at && fmtDateTime(selected.created_at)})</span></div>
                <div><span className="text-muted-foreground">Prioridad:</span> {PRIORITY_LABELS[selected.priority]?.label}</div>
                {selected.approved_by_name && <div><span className="text-muted-foreground">Aprobó:</span> {selected.approved_by_name} <span className="text-xs text-muted-foreground">({selected.approved_at && fmtDateTime(selected.approved_at)})</span></div>}
                {selected.dispensed_by_name && <div><span className="text-muted-foreground">Despachó:</span> {selected.dispensed_by_name} <span className="text-xs text-muted-foreground">({selected.dispensed_at && fmtDateTime(selected.dispensed_at)})</span></div>}
                {selected.received_by_area_name && <div><span className="text-muted-foreground">Recibió en área:</span> {selected.received_by_area_name} <span className="text-xs text-muted-foreground">({selected.received_at_area && fmtDateTime(selected.received_at_area)})</span></div>}
                {selected.rejected_by_name && <div><span className="text-muted-foreground">Rechazó:</span> {selected.rejected_by_name} <span className="text-xs text-muted-foreground">({selected.rejected_at && fmtDateTime(selected.rejected_at)})</span></div>}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artículo</TableHead>
                    <TableHead className="text-center">Ped.</TableHead>
                    <TableHead className="text-center">Aprob.</TableHead>
                    <TableHead className="text-center">Desp.</TableHead>
                    <TableHead className="text-center">Pend.</TableHead>
                    <TableHead>Lotes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(selected.items || []).map((it, i) => {
                    const dispensedTotal = it.quantity_dispensed_total ?? it.quantity_dispensed ?? 0;
                    const pending = it.quantity_pending ?? Math.max(0, (it.quantity_approved ?? 0) - dispensedTotal);
                    return (
                      <TableRow key={i}>
                        <TableCell>{it.is_medicamento && '💊 '}{it.article_name} <span className="text-xs text-muted-foreground">· {unitOf(it.article_id)}</span></TableCell>
                        <TableCell className="text-center">{it.quantity_requested} <span className="text-xs text-muted-foreground">{unitOf(it.article_id)}</span></TableCell>
                        <TableCell className="text-center">{it.quantity_approved ?? '-'}</TableCell>
                        <TableCell className="text-center font-semibold text-teal-700">{dispensedTotal}</TableCell>
                        <TableCell className="text-center">
                          {pending > 0
                            ? <span className="font-semibold text-amber-700">{pending}</span>
                            : <span className="text-emerald-600">✓</span>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {(it.allocations?.length ? it.allocations : (it.live_allocations || [])).map((a, j) => (
                            <div key={j}>Lote <b>{a.lot_number || 's/lote'}</b> ({a.expiration_date || '—'}) × {a.qty}</div>
                          ))}
                          {!it.allocations?.length && it.live_shortage > 0 && (
                            <div className="text-amber-700">Faltan {it.live_shortage}u en stock</div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {(selected.dispatches || []).length > 0 && (
                <div className="border rounded-md p-3 bg-slate-50 dark:bg-slate-900/30" data-testid="opi-dispatches-history">
                  <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase mb-2">Historial de despachos</div>
                  <div className="space-y-2">
                    {selected.dispatches.map((d) => (
                      <div key={d.dispatch_number} className="text-sm border-l-2 border-teal-500 pl-3">
                        <div className="font-medium">
                          Despacho #{d.dispatch_number} · {d.total_quantity}u
                          <span className="text-xs text-muted-foreground ml-2">
                            {d.dispatched_at && fmtDateTime(d.dispatched_at)}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">por {d.dispatched_by_name}</div>
                        <div className="text-xs mt-1">
                          {(d.items || []).map((di, k) => (
                            <span key={k} className="inline-block mr-2">• {di.article_name}: {di.quantity}u</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected.status === 'closed_partial' && selected.closed_partial_reason && (
                <div className="rounded-md border border-slate-300 bg-slate-50 dark:bg-slate-900/30 p-3">
                  <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase mb-1">Cierre con saldo pendiente</div>
                  <div className="text-sm">{selected.closed_partial_reason}</div>
                  {selected.closed_partial_by_name && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Cerrada por {selected.closed_partial_by_name}
                      {selected.closed_partial_at && ` el ${fmtDateTime(selected.closed_partial_at)}`}
                    </div>
                  )}
                </div>
              )}
              {selected.notes && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3" data-testid="opi-detail-notes">
                  <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase mb-1">Observaciones del solicitante</div>
                  <div className="text-sm whitespace-pre-wrap">{selected.notes}</div>
                </div>
              )}
              {selected.rejection_reason && <p className="text-red-600"><b>Motivo rechazo:</b> {selected.rejection_reason}</p>}
              {selected.approval_notes && <p className="text-muted-foreground"><b>Notas aprobación:</b> {selected.approval_notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Aprobar */}
      <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprobar {selected?.request_number}</DialogTitle>
            <DialogDescription>Podés ajustar la cantidad aprobada por artículo.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              {selected.notes && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3" data-testid="opi-approve-notes">
                  <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase mb-1">Observaciones del solicitante</div>
                  <div className="text-sm whitespace-pre-wrap">{selected.notes}</div>
                </div>
              )}
              {(selected.items || []).map(it => (
                <div key={it.article_id} className="flex items-center justify-between gap-2">
                  <span className="text-sm">{it.article_name} (ped. {it.quantity_requested} {unitOf(it.article_id)})</span>
                  <Input
                    type="number"
                    min="0"
                    max={it.quantity_requested}
                    className="w-24 text-right"
                    value={approveForm.items[it.article_id] ?? 0}
                    onChange={(e) => setApproveForm({ ...approveForm, items: { ...approveForm.items, [it.article_id]: parseInt(e.target.value) || 0 } })}
                  />
                  <span className="text-xs text-muted-foreground w-16 shrink-0">{unitOf(it.article_id)}</span>
                </div>
              ))}
              <div>
                <Label>Notas de aprobación</Label>
                <Textarea rows={2} value={approveForm.approval_notes} onChange={(e) => setApproveForm({ ...approveForm, approval_notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(false)}>Cancelar</Button>
            <Button onClick={doApprove} data-testid="confirm-approve-opi">Aprobar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => !open && setConfirmAction(null)}
        title={
          confirmAction?.type === 'dispense' ? 'Despachar pedido' :
          confirmAction?.type === 'delete' ? 'Eliminar pedido' :
          'Confirmar recepción'
        }
        description={
          confirmAction?.type === 'dispense' ? (
            confirmAction?.req?.status === 'partial_dispensed' ? (
              <>¿Despachar el saldo pendiente de <strong>{confirmAction?.req?.request_number}</strong>? El sistema tomará lo que haya en stock y, si sigue faltando, la OPI queda otra vez en Despacho Parcial.</>
            ) : confirmAction?.req?.has_shortage ? (
              <>La OPI <strong>{confirmAction?.req?.request_number}</strong> tiene stock insuficiente. Se va a despachar <strong>lo disponible</strong> y el saldo queda pendiente para cuando llegue mercadería.</>
            ) : (
              <>¿Despachar <strong>{confirmAction?.req?.request_number}</strong>? Esto descontará el stock real desde el depósito origen y lo espejará al destino del área.</>
            )
          ) : confirmAction?.type === 'delete' ? (
            <>¿Eliminar el pedido <strong>{confirmAction?.req?.request_number}</strong>? El pedido quedará marcado como cancelado y no podrá deshacerse. Sólo se permite eliminar pedidos que aún no fueron aprobados.</>
          ) : (
            <>¿Confirmar recepción de <strong>{confirmAction?.req?.request_number}</strong> en el área?</>
          )
        }
        confirmText={
          confirmAction?.type === 'dispense' ? 'Despachar' :
          confirmAction?.type === 'delete' ? 'Eliminar' :
          'Confirmar'
        }
        destructive={confirmAction?.type === 'dispense' || confirmAction?.type === 'delete'}
        loading={actionLoading}
        onConfirm={executeAction}
        testId="confirm-action-opi"
      />

      {/* Cerrar OPI con saldo pendiente */}
      <Dialog open={!!closePendingOpi} onOpenChange={(o) => { if (!o) { setClosePendingOpi(null); setClosePendingReason(''); } }}>
        <DialogContent data-testid="close-pending-dialog">
          <DialogHeader>
            <DialogTitle>Cerrar OPI con saldo pendiente</DialogTitle>
            <DialogDescription>
              {closePendingOpi && (
                <>
                  Vas a cerrar la OPI <strong>{closePendingOpi.request_number}</strong> renunciando al saldo no despachado.
                  El sistema dejará registro del motivo y el pedido quedará en estado <strong>"Cerrado c/ saldo"</strong>.
                  <span className="block mt-2 text-amber-700">Esta acción NO se puede deshacer.</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="close-pending-reason">Motivo *</Label>
            <Textarea
              id="close-pending-reason"
              placeholder="Ej: El área ya no requiere el saldo, se gestionó por compra externa fuera del sistema."
              value={closePendingReason}
              onChange={(e) => setClosePendingReason(e.target.value)}
              rows={4}
              data-testid="close-pending-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClosePendingOpi(null); setClosePendingReason(''); }}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={submitClosePending}
              disabled={closePendingSaving || !closePendingReason.trim()}
              data-testid="confirm-close-pending"
            >
              {closePendingSaving ? 'Cerrando...' : 'Cerrar con saldo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default InternalRequests;
